import { createFileRoute, Link, Outlet, useMatches } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import type React from 'react'

export interface LegalPageContext {
  title: string
  lastUpdated?: string
}

export const Route = createFileRoute('/_legal_pages')({
  component: RouteComponent,
})

interface LargeTextContainerProperties {
  title: string
  lastUpdated?: string
  backTo?: string
  children: React.ReactNode
}

function LargeTextContainer({
  title,
  lastUpdated,
  backTo = '/login',
  children,
}: LargeTextContainerProperties) {
  return (
    <div className="w-full bg-gray-50 pt-20 pb-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="relative bg-white/90 backdrop-blur-sm rounded-3xl border border-gray-200/50 shadow-2xl p-8 md:p-12">
          {/* Decorative corner borders */}
          <div className="absolute top-0 left-0 w-16 h-16 border-t-4 border-l-4 rounded-tl-3xl border-primary" />
          <div className="absolute top-0 right-0 w-16 h-16 border-t-4 border-r-4 rounded-tr-3xl border-secondary" />
          <div className="absolute bottom-0 left-0 w-16 h-16 border-b-4 border-l-4 rounded-bl-3xl border-secondary" />
          <div className="absolute bottom-0 right-0 w-16 h-16 border-b-4 border-r-4 rounded-br-3xl border-primary" />

          <div className="relative z-10">
            <Link
              to={backTo}
              className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary/80 hover:underline mb-6 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Login
            </Link>

            <h1 className="text-4xl font-bold text-gray-900 mb-4">{title}</h1>

            {lastUpdated && (
              <p className="text-gray-600 mb-8">
                <strong>Last update:</strong>
                <br />
                {lastUpdated}
              </p>
            )}

            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function RouteComponent() {
  const matches = useMatches()

  // Find the active child route's context
  const childRoute = matches.find(match =>
    match.routeId === '/_legal_pages/terms' || match.routeId === '/_legal_pages/privacy',
  )

  const context = childRoute?.context as LegalPageContext | undefined

  return (
    <LargeTextContainer
      title={context?.title ?? 'Legal Information'}
      lastUpdated={context?.lastUpdated}
    >
      <Outlet />
    </LargeTextContainer>
  )
}
